import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantAction, EntityRef } from '@speles7172/assistant-console';
import { invalidateTasks } from '../../hooks/useEventOps';
import { useRememberedChoice } from '../../hooks/useRememberedChoice';

const LANGUAGES = ['en', 'he'] as const;

/**
 * What every assistant screen does with a record or an action: open the
 * record's own page (the URL the server filled in — never one a model wrote),
 * and refresh what an action changed. The language the assistant writes in is
 * remembered per person, so the dashboard and the notebook agree.
 */
export function useAssistantNavigation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [language, setLanguage] = useRememberedChoice('assistant-language', LANGUAGES, 'en');

  const onOpen = useCallback(
    (entity: EntityRef) => {
      if (entity.url) navigate(entity.url);
    },
    [navigate],
  );

  const onActionDone = useCallback(
    (action: AssistantAction) => {
      if (action.kind === 'create_task') void invalidateTasks(queryClient);
    },
    [queryClient],
  );

  return { onOpen, onActionDone, language, setLanguage };
}
